// Saving and deleting vet visits, with everything they touch (see
// src/domain/vetvisit.ts).
//
// A save only writes the records whose visit details actually changed, and
// merges onto what's there — so a reminder you edited, ticked off or deleted
// in the care plan stays that way when you fix a typo in the visit's notes.

import { derivedSignature, possibleVisitIds, visitEffects } from '../domain/vetvisit.ts';
import { toISODate } from '../domain/time.ts';
import type { VetVisit } from '../domain/types.ts';
import { markCareDone, reopenCare, saveProfile } from './actions.ts';
import { data } from './index.ts';

/** Save a visit and apply its effects. Returns how many care jobs were newly ticked off. */
export function saveVetVisit(v: VetVisit, previous?: VetVisit, answeredQuestions: string[] = []): number {
  const { store } = data();
  const profile = store.get('profile', 'arlo');
  store.put('vetVisits', v);

  const eff = visitEffects(v, profile);
  const before = previous ? visitEffects(previous, profile) : undefined;
  const prevEvent = new Map(before?.events.map((e) => [e.id, derivedSignature(e)]));
  const prevCare = new Map(before?.care.map((c) => [c.id, derivedSignature(c)]));

  // Records the visit no longer wants (a removed medicine, a cleared follow-up).
  const possible = possibleVisitIds(v, previous);
  for (const id of possible.events) if (!eff.eventIds.includes(id) && store.get('events', id)) store.remove('events', id);
  for (const id of possible.care) {
    if (eff.careIds.includes(id)) continue;
    const c = store.get('care', id);
    if (c && !c.done) store.remove('care', id);
  }

  // New or changed ones only; unchanged ones are left exactly as they are (even if deleted since).
  for (const e of eff.events) {
    if (prevEvent.get(e.id) === derivedSignature(e)) continue;
    const cur = store.get('events', e.id);
    store.put('events', cur ? ({ ...cur, ...e } as typeof e) : e);
  }
  for (const c of eff.care) {
    if (prevCare.get(c.id) === derivedSignature(c)) continue;
    const cur = store.get('care', c.id);
    store.put('care', cur ? { ...cur, kind: c.kind, title: c.title, due: c.due, why: c.why } : c);
  }

  // Care-plan items ticked or unticked at this visit (repeats book / un-book their next date).
  const was = new Set(previous?.careDone ?? []);
  const now = new Set(v.careDone ?? []);
  let newlyDone = 0;
  for (const id of now) {
    if (was.has(id)) continue;
    const item = store.get('care', id);
    if (item && !item.done) {
      markCareDone(item, toISODate(v.at));
      newlyDone++;
    }
  }
  for (const id of was) if (!now.has(id)) reopenCare(id);

  // The appointment it was booked as is now written up.
  if (v.appointmentId) {
    const a = store.get('appointments', v.appointmentId);
    if (a && a.visitId !== v.id) store.put('appointments', { ...a, visitId: v.id });
  }

  // Its cost goes on the Costs page (claim progress there is kept).
  syncVisitExpense(v, previous);

  // Questions answered at the visit come off the list.
  if (answeredQuestions.length && profile?.vetQuestions?.length) saveProfile({ vetQuestions: profile.vetQuestions.filter((q) => !answeredQuestions.includes(q)) });
  return newlyDone;
}

const visitTitle = (v: VetVisit) => v.reason?.split(' — ')[0] || (v.kind === 'call' ? 'Vet call' : 'Vet visit');
const moneyOf = (v?: VetVisit) => (v ? JSON.stringify([v.cost ?? 0, toISODate(v.at), visitTitle(v), !!v.claim]) : '');

/**
 * Keep the visit's cost on the Costs page in step with the visit — only when
 * the cost, date, reason or claim choice changed, so a typo fix in the notes
 * never overwrites a title edited on Costs or a claim the other person has
 * marked as sent.
 */
function syncVisitExpense(v: VetVisit, previous?: VetVisit): void {
  if (previous && moneyOf(previous) === moneyOf(v)) return;
  const { store } = data();
  const id = `vv-${v.id}`;
  const cur = store.get('expenses', id);
  const progressed = cur?.claim && cur.claim.status !== 'to-claim' && cur.claim.status !== 'skip';
  if (!v.cost || v.cost <= 0) {
    if (cur && !progressed) store.remove('expenses', id);
    return;
  }
  // The visit's "claim it?" choice decides until a claim has actually been sent.
  const claim: NonNullable<typeof cur>['claim'] = progressed ? cur!.claim : { status: v.claim ? 'to-claim' : 'skip' };
  // A title someone changed on the Costs page stays.
  const title = cur && previous && cur.title !== visitTitle(previous) ? cur.title : visitTitle(v);
  store.put('expenses', {
    ...cur,
    id,
    date: toISODate(v.at),
    amount: Math.round(v.cost * 100) / 100,
    category: 'vet',
    title,
    visitId: v.id,
    paidBy: cur?.paidBy ?? v.by,
    claim,
    by: cur?.by ?? v.by,
  });
}

/** Delete a visit and undo it: reopen what it ticked off and remove what it added. */
export function deleteVetVisit(v: VetVisit): void {
  const { store } = data();
  const exp = store.get('expenses', `vv-${v.id}`);
  if (exp && (!exp.claim || exp.claim.status === 'to-claim' || exp.claim.status === 'skip')) store.remove('expenses', exp.id);
  if (v.appointmentId) {
    const a = store.get('appointments', v.appointmentId);
    if (a?.visitId === v.id) store.put('appointments', { ...a, visitId: undefined });
  }
  for (const id of v.careDone ?? []) reopenCare(id);
  const possible = possibleVisitIds(v);
  for (const id of possible.events) if (store.get('events', id)) store.remove('events', id);
  for (const id of possible.care) if (store.get('care', id)) store.remove('care', id);
  store.remove('vetVisits', v.id);
}
